import { FC, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import cx from "classnames";

import Button from "./../../libs/Button";
import { IconTypes } from "../../libs/Icon";

import useShallowSelector from "../../hooks/useShalowSelector";
import useSettings from "./../../hooks/useSettings";
import usePopup from "../../hooks/usePopup";
import { LoginPopupState, PopupTypes } from "../../store/types/Popup";

import { changeCurrentField, changeCurrentLoginPopupState } from "./../../store/ui/actionCreators";
import { selectFields } from "../../store/fields/selectors";
import { getActiveRoute } from "./../../helpers/getActiveRoute";
import { RouteType } from "./../routes/types";

import styles from "./styles.module.scss";

const ModeBar: FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const fieldsList = useShallowSelector(selectFields);
    const { isOpen: isSettingsOpen, onOpen: onSettingsOpen } = useSettings();
    const isGuest = useShallowSelector(state => state?.auth?.userType !== "USER");
    const { onOpen: onPopupOpen } = usePopup();

    const activeRoute = getActiveRoute(location.pathname);
    const getOpenPopupHandler = useCallback(
        (popupType: PopupTypes, newCurrentLoginPopupState: LoginPopupState) => () => {
            onPopupOpen(popupType);
            dispatch(changeCurrentLoginPopupState(newCurrentLoginPopupState));
        },
        [dispatch, onPopupOpen],
    );

    const getRouteChangeHandler = useCallback(
        (path: RouteType) => {
            dispatch(changeCurrentField(null));
            return () => {
                if (isGuest && path === RouteType.Preview) {
                    if (fieldsList && fieldsList.length === 0) {
                        onPopupOpen(PopupTypes.EMPTY_FORM);
                    } else {
                        getOpenPopupHandler(PopupTypes.LOGIN_SIGNUP, LoginPopupState.LOGIN)();
                    }
                } else {
                    navigate(path);
                }
            };
        },
        [navigate, dispatch, isGuest, getOpenPopupHandler, fieldsList, onPopupOpen],
    );

    return (
        <>
            <div className={styles.modeShadow}></div>

            <div className={styles.wrapper}>
                <div className={styles.mode}>
                    <div className={styles.bar}>
                        <Button
                            icon={IconTypes.EDIT}
                            hoverText={"Editor"}
                            iconSize={20}
                            className={cx(styles.btn, { [styles.active]: activeRoute === RouteType.Edit })}
                            onClick={getRouteChangeHandler(RouteType.Edit)}
                        />
                        <Button
                            icon={IconTypes.DESIGN}
                            hoverText={"Design"}
                            iconSize={20}
                            className={cx(styles.btn, { [styles.active]: activeRoute === RouteType.Design })}
                            onClick={getRouteChangeHandler(RouteType.Design)}
                        />

                        <Button
                            icon={IconTypes.LOGIC}
                            hoverText={"Logic"}
                            iconSize={20}
                            className={cx(styles.btn, { [styles.active]: activeRoute === RouteType.Logic })}
                            onClick={getRouteChangeHandler(RouteType.Logic)}
                        />
                        <Button
                            icon={IconTypes.VALIDATION}
                            hoverText={"Validation"}
                            iconSize={20}
                            className={cx(styles.btn, { [styles.active]: activeRoute === RouteType.Validation })}
                            onClick={getRouteChangeHandler(RouteType.Validation)}
                        />
                        <Button
                            icon={IconTypes.INTEGRATION}
                            hoverText={"Integration"}
                            iconSize={20}
                            className={cx(styles.btn, { [styles.active]: activeRoute === RouteType.Integration })}
                            onClick={getRouteChangeHandler(RouteType.Integration)}
                        />
                        <Button
                            icon={IconTypes.PAYMENT}
                            hoverText={"Payment"}
                            iconSize={20}
                            className={cx(styles.btn, { [styles.active]: activeRoute === RouteType.Payment })}
                            onClick={getRouteChangeHandler(RouteType.Payment)}
                        />
                        <Button
                            icon={IconTypes.PREVIEW}
                            hoverText={"Preview"}
                            iconSize={20}
                            className={cx(styles.btn, { [styles.active]: activeRoute === RouteType.Preview })}
                            onClick={getRouteChangeHandler(RouteType.Preview)}
                        />
                        <span className={styles.line}></span>
                        <Button
                            icon={IconTypes.SETTINGS}
                            hoverText={"Settings"}
                            iconSize={20}
                            onClick={onSettingsOpen}
                            className={cx(styles.btn, { [styles.settingsActive]: isSettingsOpen })}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

export default ModeBar;
